import { getDocument } from '@atlaskit/browser-apis';

/**
 * Drops the fragment without adding a history entry. Visitors still arrive on
 * deep links such as `#about`, so once an affordance has scrolled them back
 * to the top, leaving the fragment in place would have the URL describe a
 * section they are no longer looking at.
 *
 * Dispatches `hashchange` (rather than relying on `replaceState`, which fires
 * none natively) so `useScrollToHash` cancels any poll it has in flight for a
 * lazily-rendered section — otherwise a visitor who scrolls to an anchor
 * before its section has mounted, then clicks back to the top, can get
 * yanked back down once the poll's target finally appears.
 */
export const clearHash = (): void => {
	const view = getDocument()?.defaultView;
	if (!view) {
		return;
	}
	const { pathname, search } = view.location;
	// Read before `replaceState`, so a listener diffing `oldURL`/`newURL` sees the
	// fragment being dropped. Nothing here reads them, but other `hashchange`
	// listeners on the page are entitled to.
	const oldURL = view.location.href;
	view.history.replaceState(view.history.state, '', `${pathname}${search}`);
	view.dispatchEvent(
		new view.HashChangeEvent('hashchange', { oldURL, newURL: view.location.href }),
	);
};
